
import { useState, useCallback, useRef } from 'react';
import { MortgageData, CalculatorHandlers, UIState } from '@/types/calculator';
import { calculateMortgageData } from '@/utils/calculator/mortgageCalculator';
import { useInitialMortgageData } from './calculator/useInitialMortgageData';
import { useCalculatorHandlers } from './calculator/useCalculatorHandlers';
import { useCalculatorValidation } from './calculator/useCalculatorValidation';
import { useCalculatorLogger } from './calculator/useCalculatorLogger';

export const useUnifiedCalculator = () => {
  const initialData = useInitialMortgageData();
  const [mortgageData, setMortgageDataState] = useState<MortgageData>(initialData);
  const [isCalculating, setIsCalculating] = useState(false);
  
  // Initialize logger
  const logger = useCalculatorLogger('UnifiedCalculator');
  
  // Batch update mechanism to prevent rapid state changes
  const pendingUpdateRef = useRef<Partial<MortgageData> | null>(null);
  const updateTimeoutRef = useRef<number | null>(null);
  const isUpdatingRef = useRef(false);
  const previousDataRef = useRef<MortgageData>(initialData);

  // Create a proper setMortgageData function that handles both partial updates and full state replacement
  const setMortgageData = useCallback((action: React.SetStateAction<MortgageData>) => {
    // Prevent overlapping calculations
    if (isUpdatingRef.current) {
      console.log('[useUnifiedCalculator] Skipping overlapping update');
      return;
    }
    
    isUpdatingRef.current = true;
    setIsCalculating(true);
    
    setMortgageDataState(prev => {
      let updated: MortgageData;
      
      if (typeof action === 'function') {
        updated = action(prev);
      } else {
        updated = action;
      }
      
      // Log changes
      const changedFields = Object.keys(updated).filter(
        key => updated[key as keyof MortgageData] !== prev[key as keyof MortgageData]
      );
      changedFields.forEach(field => {
        logger.logChange(
          field,
          prev[field as keyof MortgageData],
          updated[field as keyof MortgageData],
          'setMortgageData'
        );
      });
      
      const calculated = logger.trackCalculation('mortgageData', () => 
        calculateMortgageData(updated)
      );
      
      previousDataRef.current = calculated;
      
      // Use requestAnimationFrame to batch the state update completion
      requestAnimationFrame(() => {
        setIsCalculating(false);
        isUpdatingRef.current = false;
      });
      
      return calculated;
    });
  }, [logger]);

  const updateData = useCallback((updates: Partial<MortgageData>) => {
    // Clear any pending batched update
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    // Accumulate updates
    pendingUpdateRef.current = {
      ...(pendingUpdateRef.current || {}),
      ...updates
    };
    
    // Batch updates with a small delay
    updateTimeoutRef.current = window.setTimeout(() => {
      if (pendingUpdateRef.current) {
        const batchedUpdates = pendingUpdateRef.current;
        pendingUpdateRef.current = null;
        setMortgageData(prev => ({ ...prev, ...batchedUpdates }));
      }
    }, 16); // One frame delay for batching
  }, [setMortgageData]);

  // Get handlers from the dedicated hook
  const handlers = useCalculatorHandlers({ mortgageData, updateData });

  // Get validation state
  const { uiState } = useCalculatorValidation(mortgageData, isCalculating);

  return {
    mortgageData,
    handlers,
    uiState,
    setMortgageData,
    logger, // Expose logger for debugging
  };
};
